import React, { useEffect, useRef } from 'react';
import { DotLottie } from '@lottiefiles/dotlottie-web';
// @ts-ignore
import wasmUrl from '@lottiefiles/dotlottie-web/dist/dotlottie-player.wasm?url';
import { resolveAssetUrl } from '@web-chat-core/ui-kit/helpers/electron';
import { LottieCanvas } from './view';
import { getLottieFile } from './useLottieCache';
import { lottieWorkerManager } from './LottieWorkerManager';

// Set the WASM URL for fallback
DotLottie.setWasmUrl(resolveAssetUrl(wasmUrl));

interface LottieReactionProps {
    animationFile: string;
}

const supportsOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';

let idCounter = 0;
const generateId = () => `lottie-${++idCounter}`;

const LottieReactionWorker: React.FC<LottieReactionProps> = ({ animationFile }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const idRef = useRef(generateId());
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        const canvas = canvasRef.current;
        const id = idRef.current;

        if (!canvas) return;

        let offscreen: OffscreenCanvas | null = null;

        const init = async () => {
            try {
                const animationData = await getLottieFile(animationFile);
                if (!mountedRef.current || !canvas) return;

                // transferControlToOffscreen можно вызвать только один раз
                offscreen = canvas.transferControlToOffscreen();
                await lottieWorkerManager.initAnimation(id, offscreen, animationData);
            } catch (error) {
                // Игнорируем ошибки если компонент уже размонтирован
                if (mountedRef.current) {
                    console.error('[LottieReactionWorker] Failed to init:', error);
                }
            }
        };

        init();

        return () => {
            mountedRef.current = false;
            lottieWorkerManager.destroyAnimation(id);
        };
    }, [animationFile]);

    return <LottieCanvas ref={canvasRef} />;
};

// Main thread fallback implementation
const LottieReactionFallback: React.FC<LottieReactionProps> = ({ animationFile }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const instanceRef = useRef<DotLottie | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const init = async () => {
            try {
                const animationData = await getLottieFile(animationFile);
                if (!mountedRef.current || !canvas) return;

                instanceRef.current = new DotLottie({
                    canvas,
                    data: animationData,
                    autoplay: true,
                    loop: true,
                    renderConfig: {
                        devicePixelRatio: 1,
                    },
                });
            } catch (error) {
                if (mountedRef.current) {
                    console.error('[LottieReactionFallback] Failed to init:', error);
                }
            }
        };

        init();

        return () => {
            mountedRef.current = false;
            if (instanceRef.current) {
                instanceRef.current.destroy();
                instanceRef.current = null;
            }
        };
    }, [animationFile]);

    return <LottieCanvas ref={canvasRef} />;
};

export const LottieReaction: React.FC<LottieReactionProps> = (props) => {
    if (supportsOffscreenCanvas) {
        return <LottieReactionWorker {...props} />;
    }
    return <LottieReactionFallback {...props} />;
};
